import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/hooks/useAlert";
import {
  FileRejectionCode,
  FileUploadConfig,
  GAILGPT_FILE_LIMITS,
  applyGailGptLimits,
  formatFileSize,
  getFileExtensionFromMimeType,
  validateFilesWithFeedbackAsync,
} from "@/utils/fileHelpers";

export const useFileUpload = (
  fileUploadConfig: Partial<FileUploadConfig>,
  assistantModel?: string
) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const { setAlert } = useAlert();
  const { t } = useTranslation("ask");

  const handleFilesSelected = async (files: File[]) => {
    const maxFileCount = fileUploadConfig.maxFileCount ?? 5;
    const totalFilesAfterAdd = attachedFiles.length + files.length;

    // First check file count limits
    if (totalFilesAfterAdd > maxFileCount) {
      const allowedNewFiles = maxFileCount - attachedFiles.length;
      if (allowedNewFiles <= 0) {
        setAlert({
          status: "warning",
          message: t("maxAmountOfFilesAllowed", {
            maxFileCount,
          }),
          secondsToClose: 10,
        });
        return;
      } else {
        setAlert({
          status: "warning",
          message: t("canOnlyAddMoreFiles", {
            allowedNewFiles,
            maxFileCount,
          }),
          secondsToClose: 10,
        });
        files = files.slice(0, allowedNewFiles);
      }
    }

    // Filter out duplicate files
    const newFiles = files.filter(
      (newFile) =>
        !attachedFiles.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
        )
    );

    if (newFiles.length === 0) {
      return;
    }

    // Create unified config for validation — GailGPT chat limits
    const unifiedConfig = applyGailGptLimits({
      enabled: fileUploadConfig.enabled ?? true,
      acceptedFileTypes: fileUploadConfig.acceptedFileTypes ?? "*/*",
      maxFileCount: fileUploadConfig.maxFileCount ?? 5,
    }, assistantModel);

    const { validFiles } = await validateFilesWithFeedbackAsync(
      newFiles,
      unifiedConfig,
      assistantModel,
      {
        showAlerts: true,
        setAlert,
        t,
      }
    );

    if (validFiles.length === 0) {
      return;
    }

    // Aggregate-size enforcement: per-file validation only checks each file
    // in isolation, so a user could attach N files that individually pass
    // but together exceed the total cap. Take files in order until the
    // cap is hit and reject the rest.
    const totalMaxFileSize = unifiedConfig.totalMaxFileSize;
    const existingTotal = attachedFiles.reduce((sum, f) => sum + f.size, 0);
    const acceptedFiles: File[] = [];
    let runningTotal = existingTotal;
    let droppedForTotal = 0;
    for (const file of validFiles) {
      if (runningTotal + file.size > totalMaxFileSize) {
        droppedForTotal += 1;
        continue;
      }
      acceptedFiles.push(file);
      runningTotal += file.size;
    }

    if (droppedForTotal > 0) {
      setAlert({
        status: "warning",
        message: t("filesExceedTotalSize", {
          count: droppedForTotal,
          maxSize: formatFileSize(totalMaxFileSize),
          defaultValue: `{{count}} file(s) skipped — total attachments would exceed {{maxSize}}`,
        }),
        secondsToClose: 4,
      });
    }

    if (acceptedFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...acceptedFiles]);
    }
  };

  const handleFilesChange = (files: File[]) => {
    setAttachedFiles(files);
  };

  const handleInvalidFiles = (
    rejectedFiles: { file: File; reason: string; code?: FileRejectionCode }[]
  ) => {
    const firstCode = rejectedFiles[0]?.code;
    let message: string;

    switch (firstCode) {
      case FileRejectionCode.FILE_TOO_LARGE: {
        const maxSize = GAILGPT_FILE_LIMITS.maxFileSize;
        message = t("filesTooLarge", {
          count: rejectedFiles.filter((r) => r.code === FileRejectionCode.FILE_TOO_LARGE).length,
          maxSize: `${Math.round(maxSize / (1024 * 1024))}MB`,
        });
        break;
      }
      case FileRejectionCode.TYPE_NOT_SUPPORTED:
        message = t("filesUnsupportedType", {
          count: rejectedFiles.filter((r) => r.code === FileRejectionCode.TYPE_NOT_SUPPORTED).length,
        });
        break;
      case FileRejectionCode.UPLOADS_NOT_SUPPORTED:
        message = t("filesNotSupported", {
          count: rejectedFiles.filter((r) => r.code === FileRejectionCode.UPLOADS_NOT_SUPPORTED).length,
        });
        break;
      default:
        message = t("filesNotSupported", {
          count: rejectedFiles.length,
        });
    }

    setAlert({
      status: "warning",
      message,
      secondsToClose: 4,
    });
  };

  const clearAttachedFiles = () => {
    setAttachedFiles([]);
  };

  return {
    attachedFiles,
    handleFilesSelected,
    handleFilesChange,
    handleInvalidFiles,
    clearAttachedFiles,
    getFileExtensionFromMimeType,
  };
};
